import { Classes, Intent } from "@blueprintjs/core";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import {
  BigMenuItem,
  BigMenuItemTextRenderer,
  HexButton,
  HexCallout,
  HexMenu,
  HexPopover,
} from "../../../../hex-components";
import {
  AttentionIcon,
  SingleChevronDownIcon,
  TickIcon,
} from "../../../icons/CustomIcons";
import { BaseInputConfiguration } from "../../../input/config/BaseInputConfiguration";
import { InputConfigurationComponent } from "../../../input/config/InputConfigurationComponent";

const StyledPopover = styled(HexPopover)`
  width: 100%;
`;

const DropdownOption = styled(HexButton)<{ $capitalize: boolean }>`
  justify-content: space-between;
  width: 100%;

  ${({ $capitalize }) => $capitalize && "text-transform: capitalize;"}

  outline: none;

  .${Classes.BUTTON_TEXT} {
    line-height: 15px;
  }
`;

const StyledMenu = styled(HexMenu)`
  min-width: 210px;
  max-height: 300px;
  overflow-y: auto;
`;

const StyledMenuItem = styled(BigMenuItem)<{ $capitalize: boolean }>`
  ${({ $capitalize }) =>
    $capitalize && "&:not(:has(*))::first-line { text-transform: capitalize; }"}

  width: 100%;
`;

export const DropdownFormField: InputConfigurationComponent<string> =
  React.memo(function DropdownFormField({
    disabled,
    dropdownWarning,
    error,
    info,
    isRequired,
    label,
    onChange,
    options,
    placeholder,
    value,
  }) {
    return (
      <BaseInputConfiguration
        disabled={disabled}
        error={error}
        info={info}
        isRequired={isRequired}
        label={label}
      >
        <Dropdown
          disabled={disabled}
          dropdownWarning={dropdownWarning}
          options={options}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      </BaseInputConfiguration>
    );
  });

export const Dropdown: InputConfigurationComponent<string> = React.memo(
  function Dropdown({
    className,
    disabled,
    dropdownWarning,
    onChange,
    options,
    placeholder,
    value,
  }) {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    const changeCallback = useCallback((item) => onChange(item), [onChange]);

    const selectedOption = options?.find((option) => option.value === value);
    const selectedLabel = selectedOption?.label;

    const itemMenu = (
      <StyledMenu>
        {dropdownWarning && (
          <HexCallout
            $size="small"
            icon={<AttentionIcon />}
            intent={Intent.WARNING}
          >
            {dropdownWarning}
          </HexCallout>
        )}
        {options?.map((item) => (
          <StyledMenuItem
            key={item.value}
            $capitalize={item.capitalize ?? true}
            labelElement={item.value === value ? <TickIcon /> : undefined}
            text={
              <BigMenuItemTextRenderer
                active={item.value === value}
                description={item.description}
                name={
                  typeof item.label === "string" && (item.capitalize ?? true)
                    ? item.label.charAt(0).toUpperCase() + item.label.slice(1)
                    : item.label
                }
              />
            }
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => changeCallback(item.value)}
          />
        ))}
      </StyledMenu>
    );

    const shouldCapitalizeOption = useMemo(() => {
      if (selectedLabel) {
        return selectedOption?.capitalize ?? true;
      }
      return false;
    }, [selectedLabel, selectedOption?.capitalize]);

    return (
      <StyledPopover
        className={className}
        content={itemMenu}
        isOpen={isMenuOpen}
        matchTargetWidth={true}
        minimal={true}
        placement="bottom"
        // eslint-disable-next-line react/jsx-no-bind
        onClose={() => setIsMenuOpen(false)}
      >
        <DropdownOption
          $capitalize={shouldCapitalizeOption}
          active={isMenuOpen}
          className={Classes.INPUT}
          disabled={disabled}
          rightIcon={<SingleChevronDownIcon />}
          small={false}
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {selectedLabel || placeholder || "Select"}
        </DropdownOption>
      </StyledPopover>
    );
  },
);
